import { useState, useEffect, createContext, useContext } from 'react';
import Cookies from 'js-cookie';

/**
 * Auth Context
 * Manages authentication state from Auth Microservice cookies
 */
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = () => {
    try {
      // Read user cookie set by Auth Microservice
      const userCookie = Cookies.get('user');
      
      if (!userCookie) {
        setUser(null);
        setLoading(false);
        return;
      }

      // Parse user data
      const userData = JSON.parse(decodeURIComponent(userCookie));

      // Check if user has CRM access
      if (!userData.isCrm) {
        setUser(null);
        setLoading(false);
        return;
      }

      setUser({
        id: userData.id,
        email: userData.email,
        name: userData.name,
        role: userData.role,
        isCrm: userData.isCrm,
        isBrsr: userData.isBrsr,
        isSurvey: userData.isSurvey,
        isLms: userData.isLms,
      });
    } catch (error) {
      console.error('Auth check error:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Call Auth Microservice logout API
      const authBackendUrl = process.env.REACT_APP_AUTH_URL || 'http://localhost:4000';
      
      await fetch(`${authBackendUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include', // Important: Send cookies to Auth Microservice
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with logout even if API call fails
    }
    
    // Clear local cookies
    const cookiesToRemove = [
      'token',
      'refreshToken', 
      'userId',
      'user',
      'brsrAccess',
      'lmsAccess',
      'crmAccess',
      'surveyAccess',
      'accessLevel',
      'role',
      'company',
      'userData'
    ];
    
    cookiesToRemove.forEach(cookieName => {
      Cookies.remove(cookieName);
    });
    
    // Redirect to Auth UI login page
    const authUiUrl = process.env.REACT_APP_AUTH_UI_URL || 'http://localhost:5173';
    window.location.href = `${authUiUrl}/login`;
  };

  const redirectToLogin = () => {
    const authUiUrl = process.env.REACT_APP_AUTH_UI_URL || 'http://localhost:5173';
    const currentUrl = window.location.href;
    window.location.href = `${authUiUrl}/login?redirect=${encodeURIComponent(currentUrl)}`;
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'SUPERADMIN',
    logout,
    redirectToLogin,
    refetch: checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * useAuth Hook
 * Access auth state and methods
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  
  return context;
};

export default useAuth;


